import axios, { AxiosInstance } from "axios";
import cookie from "js-cookie";
import { has } from "lodash";

export interface ProviderInterface {
  client: AxiosInstance;
  clientBlob: AxiosInstance;
  clientUpload: AxiosInstance;
  clientSSE: AxiosInstance;
  apiURL: string;
  token: string | undefined;
  setInterceptors: (token: string) => void;
}

export interface ApiProviderFactory {
  (baseUrl?: string): ProviderInterface;
  provider?: ProviderInterface;
}

const ApiProvider: ApiProviderFactory = function (
  baseUrl?: string
): ProviderInterface {
  const token = cookie.get("dropla_auth");

  if (
    ApiProvider.provider &&
    ApiProvider.provider.apiURL === baseUrl &&
    ApiProvider.provider.token === token
  ) {
    return ApiProvider.provider;
  }

  const client = axios.create({
    baseURL: baseUrl,
    headers: { "Content-Type": "application/json" }
  });

  const clientBlob = axios.create({
    baseURL: baseUrl,
    headers: { "Content-Type": "application/json" },
    responseType: "blob"
  });

  const clientSSE = axios.create({
    baseURL: baseUrl,
    headers: { Accept: "text/event-stream" },
    responseType: "stream",
    adapter: "fetch"
  });

  const clientUpload = axios.create({
    baseURL: baseUrl,
    headers: { "Content-Type": "multipart/form-data" }
  });

  const setInterceptors = (token: string) => {
    [client, clientBlob, clientSSE, clientUpload].forEach((instance) => {
      instance.interceptors.request.use((config) => {
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      });
    });

    clientUpload.interceptors.response.use((response) => {
      response.data = has(response.data, "data")
        ? response.data.data
        : response.data;
      return response;
    });
  };

  if (token) {
    setInterceptors(token);
  }

  ApiProvider.provider = {
    client,
    clientBlob,
    clientUpload,
    clientSSE,
    apiURL: baseUrl ?? "",
    token,
    setInterceptors
  };

  return ApiProvider.provider;
};

export default ApiProvider;
